import { AlertTriangle, Check, ExternalLink, Loader2 } from "lucide-react"
import { useState } from "react"

import { verifyMentionHandle } from "@/api/mention-handles"
import PlatformIcon from "@/components/icons/platform-icon"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { handleValidationState } from "@/lib/mention-handle-validation"
import { platformProfilePath } from "@/routes"
import type { MentionHandleVerificationStatus, Platform } from "@/types"

// Server-reported statuses plus the client-only states while idle/in-flight.
type VerificationStatus = MentionHandleVerificationStatus | "idle" | "checking"

interface MentionHandleFieldsProps {
  mentionPlatforms: Platform[]
  // Handle values keyed by platform.name.
  handles: Record<string, string>
  onHandleChange: (platformName: string, value: string) => void
  // Placeholder shown in empty inputs, e.g. "@alice".
  placeholderLabel?: string
}

export default function MentionHandleFields({
  mentionPlatforms,
  handles,
  onHandleChange,
  placeholderLabel,
}: MentionHandleFieldsProps) {
  const [verifications, setVerifications] = useState<
    Record<string, VerificationStatus>
  >({})

  async function handleVerify(platform: Platform, handle: string) {
    setVerifications((prev) => ({ ...prev, [platform.name]: "checking" }))
    const result = await verifyMentionHandle(platform.name, handle)
    setVerifications((prev) => ({
      ...prev,
      [platform.name]: result.ok ? (result.data.status ?? "error") : "error",
    }))
  }

  return (
    <div className="flex flex-col gap-2">
      {mentionPlatforms.map((platform) => {
        const handle = handles[platform.name] ?? ""
        // Handles are stored with a leading "@", but a fixed "@" adornment is
        // shown to the left of the input so the user never types it.
        const displayValue = handle.replace(/^@/, "")
        const validation = handleValidationState(platform, handle)
        const isValid = !!handle.trim() && validation?.valid !== false
        const profileUrl = isValid
          ? platformProfilePath(platform.id, { handle })
          : null
        const supportsVerification = platform.supports_handle_verification
        const verificationStatus = verifications[platform.name] ?? "idle"

        return (
          <div key={platform.name} className="flex items-start gap-3">
            <Label className="flex w-24 shrink-0 items-center gap-1.5 pt-2 text-xs">
              <PlatformIcon platform={platform.name} className="size-3.5" />
              {platform.label}
            </Label>
            <div className="flex flex-1 items-start gap-2">
              <div className="relative flex-1">
                <span className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-sm select-none">
                  @
                </span>
                <Input
                  value={displayValue}
                  onChange={(e) => {
                    const stripped = e.target.value.replace(/^@+/, "")
                    onHandleChange(
                      platform.name,
                      stripped === "" ? "" : `@${stripped}`,
                    )
                    setVerifications((prev) => {
                      if (!prev[platform.name]) return prev
                      const next = { ...prev }
                      delete next[platform.name]
                      return next
                    })
                  }}
                  placeholder={(
                    placeholderLabel ?? `@${platform.name}`
                  ).replace(/^@/, "")}
                  className="h-8 pl-7 text-sm"
                />
              </div>
              {profileUrl && (
                <a
                  href={profileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={`Open ${platform.label} profile in new tab`}
                  className="text-muted-foreground hover:text-foreground inline-flex size-8 shrink-0 items-center justify-center"
                >
                  <ExternalLink className="size-3.5" />
                </a>
              )}
              {supportsVerification && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-8 shrink-0"
                  disabled={!isValid || verificationStatus === "checking"}
                  onClick={() => void handleVerify(platform, handle)}
                >
                  {verificationStatus === "checking" ? (
                    <Loader2 className="size-3.5 animate-spin" />
                  ) : (
                    "Check"
                  )}
                </Button>
              )}
              {validation && !validation.valid && (
                <span className="text-destructive inline-flex min-h-8 items-center text-xs">
                  {validation.message}
                </span>
              )}
              {validation?.valid && verificationStatus === "verified" && (
                <span className="inline-flex min-h-8 shrink-0 items-center gap-1 text-xs whitespace-nowrap text-green-700">
                  <Check className="size-3.5" />
                  Found
                </span>
              )}
              {validation?.valid && verificationStatus === "not_found" && (
                <span className="inline-flex min-h-8 shrink-0 items-center gap-1 text-xs whitespace-nowrap text-amber-700">
                  <AlertTriangle className="size-3.5" />
                  Not found
                </span>
              )}
              {validation?.valid && verificationStatus === "error" && (
                <span className="text-muted-foreground inline-flex min-h-8 shrink-0 items-center text-xs whitespace-nowrap">
                  Couldn&apos;t check
                </span>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}
